import React, { useState } from 'react';
import { ArrowRight, Download, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ClientTestimonial from '../components/ClientTestimonial';
import { trustedByLogos } from '../data/mock';
import { HeroCanvas } from '../components/HeroCanvas';
import { ImpactFilmModal } from '../components/ImpactFilm';
import { Reveal } from '../components/home/Reveal';
import { FragmentCards } from '../components/home/FragmentCards';
import { RingsAnimated } from '../components/home/RingsAnimated';
import { WhenOrganisationsCall } from '../components/home/WhenOrganisationsCall';
import { EngagementFlow } from '../components/home/EngagementFlow';

// Organization structured data (homepage) - RESILIGENT + owned product Impact AS Plus
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RESILIGENT",
  "url": "https://resiligent.com",
  "logo": "https://resiligent.com/opt/brand/resiligent-logo-rebrand-06.webp",
  "address": [
    {
      "@type": "PostalAddress",
      "addressLocality": "Lagos",
      "addressRegion": "Lagos State",
      "addressCountry": "NG"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "London",
      "addressCountry": "GB"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "Dubai",
      "addressCountry": "AE"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/resiligent",
    "https://web.facebook.com/resiligent",
    "https://www.instagram.com/resiligent/",
    "https://twitter.com/resiligent1"
  ],
  "owns": {
    "@type": "Product",
    "name": "Impact AS Plus",
    "url": "https://impactasportal.com"
  }
};

const HomePage = () => {
  const [showImpactFilm, setShowImpactFilm] = useState(false);

  // Corporate profile download modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadForm, setDownloadForm] = useState({ name: '', company: '', email: '' });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Current corporate profile file — replace this single URL to swap in a new profile.
  const CORPORATE_PROFILE_URL = "https://customer-assets.emergentagent.com/job_9dea0385-ce0b-4daa-adfc-ca84c0eb189a/artifacts/e56czl5s_RESILIGENT%20Corporate%20Profile%20%272026.pdf";

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    setDownloadLoading(true);
    setDownloadError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/profile-downloads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(downloadForm)
      });

      if (response.ok) {
        window.open(CORPORATE_PROFILE_URL, '_blank');
        setShowDownloadModal(false);
        setDownloadForm({ name: '', company: '', email: '' });
      } else {
        setDownloadError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setDownloadError('Something went wrong. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Proof band logos (homepage only) - ordered rows; excludes Secondary and Webclip
  const logoByName = Object.fromEntries(trustedByLogos.map((l) => [l.name, l]));
  const africaRowLogos = [
    "Central Bank of Nigeria", "African Development Bank", "Bank of Industry", "Union Bank",
    "Polaris Bank", "NLNG", "Lagos Business School", "Delta State", "Life Flour Mill",
    "Fenchurch Group", "Fenchurch Energy", "Fenchurch Power", "Kedari Capital", "MBO Capital", "Michelson"
  ].map((n) => logoByName[n]).filter(Boolean);
  const globalRowLogos = [
    "Deutsche Bank", "Barclays", "BNP Paribas", "Citi", "Scotiabank", "Amazon", "Vodafone",
    "Fujitsu", "Coca-Cola", "Danone", "JTI", "Irish Distillers", "Booking.com", "Molten Ventures", "Utilita"
  ].map((n) => logoByName[n]).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Development Nigeria | Executive Coaching Lagos | RESILIGENT Africa"
        description="RESILIGENT is Nigeria's leading leadership development and executive coaching firm. ICF MCC certified coaches delivering leadership intelligence, diagnostics, and organisational transformation across Africa. Trusted by top Nigerian banks, multinationals, and government agencies."
        keywords="leadership development Nigeria, executive coaching Lagos, leadership consulting Africa, organisational development Nigeria, ICF certified coach Nigeria, leadership training Lagos, executive coaching West Africa, leadership intelligence Africa, board advisory Nigeria, leadership diagnostics Lagos"
        canonical="/"
        jsonLd={organizationSchema}
      />

      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[10000] focus:bg-white focus:text-[#2693d0] focus:px-4 focus:py-2 focus:rounded focus:font-medium"
      >
        Skip to main content
      </a>

      <Header />

      <main id="main-content" role="main">
        {/* 1. HERO SECTION (canvas, animation and palette unchanged) */}
        <section 
          className="relative min-h-[100svh] sm:min-h-[85vh] flex items-center bg-[#0a0a0a] overflow-hidden"
          aria-labelledby="hero-heading"
        >
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <HeroCanvas />
          </div>

          <style>{`
            .hero-canvas {
              width: 150%;
              animation: hero-drift 30s linear infinite alternate;
            }
            @keyframes hero-drift {
              from { transform: translateX(0); }
              to { transform: translateX(-33%); }
            }
            @media (prefers-reduced-motion: reduce) {
              .hero-canvas {
                animation: none;
              }
            }
          `}</style>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
            <div className="max-w-4xl mx-auto text-center">
              <h1 
                id="hero-heading"
                className="text-white font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] sm:leading-[1.05] tracking-tight mb-6 sm:mb-8"
              >
                Leadership intelligence that moves organisations
              </h1>

              <p className="text-slate-300 text-lg sm:text-xl lg:text-2xl leading-relaxed mb-8 sm:mb-10 max-w-3xl mx-auto">
                RESILIGENT is a leadership intelligence firm. From Lagos, London and Dubai we help organisations see their leaders clearly and make better decisions about people, at every level.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 sm:gap-3 bg-[#2693d0] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 focus:ring-offset-[#0a0a0a] transition-all duration-300"
                  data-testid="hero-decision-btn"
                >
                  Tell us the decision you are facing
                  <ArrowRight size={20} aria-hidden="true" />
                </a>
                <button
                  type="button"
                  onClick={() => setShowImpactFilm(true)}
                  className="inline-flex items-center gap-2 sm:gap-3 bg-white/10 text-white border border-white/30 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  data-testid="hero-watch-film-btn"
                >
                  Watch the film
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 2. SCROLLING LOGO STRIP (unchanged) */}
        <section className="bg-[#f8fafb] py-6 sm:py-8 overflow-hidden" aria-label="Some of the organisations we have worked with">
          <p className="text-slate-400 text-xs sm:text-sm text-center mb-4 sm:mb-5">
            Some of the organisations we have worked with
          </p>
          <div 
            className="logo-scroll-container group"
            onMouseEnter={(e) => {
              const inner = e.currentTarget.querySelector('.logo-scroll-inner');
              if (inner) inner.style.animationPlayState = 'paused';
            }}
            onMouseLeave={(e) => {
              const inner = e.currentTarget.querySelector('.logo-scroll-inner');
              if (inner) inner.style.animationPlayState = 'running';
            }}
          >
            <div className="logo-scroll-inner flex items-center gap-10 sm:gap-14">
              {[...africaRowLogos, ...globalRowLogos, ...africaRowLogos, ...globalRowLogos].map((logo, index) => (
                <img
                  key={`scroll-logo-${index}`}
                  src={logo.url}
                  alt={logo.name}
                  className="h-8 sm:h-10 w-auto object-contain flex-shrink-0 opacity-70 grayscale-0"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
          <style>{`
            .logo-scroll-container {
              width: 100%;
              overflow: hidden;
            }
            .logo-scroll-inner {
              display: flex;
              width: max-content;
              animation: scroll-logos 60s linear infinite;
            }
            @keyframes scroll-logos {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @media (prefers-reduced-motion: reduce) {
              .logo-scroll-inner {
                animation: none;
              }
            }
          `}</style>
        </section>

        {/* INTRODUCING RESILIGENT VIDEO SECTION (restored) */}
        <section className="bg-[#0a0a0a] py-12 sm:py-16 lg:py-24" aria-labelledby="video-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-white/60 text-xs sm:text-sm font-medium uppercase tracking-wider mb-3 sm:mb-4">
                Introducing RESILIGENT
              </p>
              <h2 id="video-heading" className="text-white font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl mx-auto mb-3 sm:mb-4">
                A leadership advisory firm built on evidence, experience, and impact
              </h2>
              <p className="text-white/70 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
                Discover how we work with organisations and the tangible outcomes we enable leaders to achieve.
              </p>
            </div>

            {/* Video Container */}
            <div className="relative max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-lg sm:rounded-xl overflow-hidden bg-[#0a1628] shadow-2xl">
                <video 
                  id="intro-video"
                  className="w-full h-full object-cover"
                  preload="metadata"
                  playsInline
                  controls
                  poster="/opt/photo/screenshot-2026-01-08-at-19-25-48.webp"
                  aria-label="RESILIGENT introduction video"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_f1101ff4-5c3a-4b22-9c23-7958ffd82067/artifacts/xwcxxl6x_Resiligent%20Intro%20Video.mp4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>

                {/* Play Button Overlay */}
                <button 
                  id="video-overlay"
                  className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-inset"
                  onClick={() => {
                    const video = document.getElementById('intro-video');
                    const overlay = document.getElementById('video-overlay');
                    if (video && overlay) {
                      video.play();
                      overlay.style.opacity = '0';
                      overlay.style.pointerEvents = 'none';
                    }
                  }}
                  aria-label="Play introduction video"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-black/40 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center transition-all duration-300 hover:bg-black/50 hover:scale-110">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <span className="text-white text-xs sm:text-sm lg:text-base font-medium mt-3 sm:mt-4 drop-shadow-lg">
                    Watch the introduction →
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 3. THE RECORD THAT SHOULD ALREADY EXIST */}
        <section className="bg-white py-14 sm:py-20 lg:py-28" aria-labelledby="record-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
              <Reveal>
                <h2 id="record-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-6 leading-tight">
                  The record that should already exist
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-5">
                  The board asks who could run the East Africa business, and somebody goes looking. There is a psychometric from the candidate's hire four years ago, run by a firm nobody uses now, a 360 from a leadership programme, an appraisal rating, and a development plan that was written, filed and never opened again.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-5">
                  None of it was collected for this question, so the answer that goes back to the board gets built on a Thursday evening out of four fragments and what three people can remember. It might be right. Nobody can show why, and nobody will know for two years.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Your finance team can trace any figure back through the ledger to the transaction that created it, and risk can trace a number back to the exposure, the limit and the person who signed it. Leadership was never built that way, which is why the questions keep arriving at short notice and the answers keep being assembled by hand.
                </p>
              </Reveal>
              <Reveal delay={120}>
                <FragmentCards />
              </Reveal>
            </div>
          </div>
        </section>

        {/* 4. WE BUILD THE RECORD THAT SHOULD ALREADY BE THERE */}
        <section className="relative py-14 sm:py-20 lg:py-28 overflow-hidden" aria-labelledby="build-heading" style={{ background: 'radial-gradient(ellipse at 70% 50%, #185088 0%, #0E2A4D 45%, #07152D 100%)' }}>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
              <Reveal>
                <h2 id="build-heading" className="text-white font-bold text-2xl sm:text-3xl md:text-4xl mb-6 leading-tight">
                  We build the record that should already be there
                </h2>
                <p className="text-white/80 text-base lg:text-lg leading-relaxed mb-8">
                  Impact AS Plus™ is the platform we built and own. It holds each leader's assessments and reports in one place, with their own access to them, and it carries what we hold at three levels.
                </p>
                <div className="space-y-5 mb-8">
                  <p className="text-white/80 text-base lg:text-lg leading-relaxed">
                    <span className="font-semibold text-white">The leader.</span> How they behave and decide under pressure, how they reason, and how others experience them.
                  </p>
                  <p className="text-white/80 text-base lg:text-lg leading-relaxed">
                    <span className="font-semibold text-white">The team.</span> How decisions actually get made, and where trust is thin.
                  </p>
                  <p className="text-white/80 text-base lg:text-lg leading-relaxed">
                    <span className="font-semibold text-white">The organisation.</span> The culture your leadership is creating, how deep the bench runs, and where capability is uneven.
                  </p>
                </div>
                <p className="text-white/80 text-base lg:text-lg leading-relaxed mb-8">
                  Evidence gathered for one decision stays available to the next, under the access and retention terms we agree with you, so the second piece of work starts from what the first one found.
                </p>
                <a 
                  href="/impact-as-plus"
                  className="inline-flex items-center gap-2 text-[#2EB6E8] font-semibold text-base hover:gap-3 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2EB6E8] focus:ring-offset-2 focus:ring-offset-[#0E2A4D]"
                >
                  See how Impact AS Plus™ works
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              </Reveal>
              <Reveal delay={120}>
                <RingsAnimated className="w-full" />
              </Reveal>
            </div>
          </div>
        </section>

        {/* 5. WHEN ORGANISATIONS CALL US */}
        <section className="bg-white py-14 sm:py-20 lg:py-28" aria-labelledby="call-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 id="call-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-8 leading-tight">
                When organisations call us
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <WhenOrganisationsCall />
            </Reveal>
          </div>
        </section>

        {/* 6. WHY IT COMES FROM OUTSIDE (dark band) */}
        <section className="bg-[#0a1628] py-14 sm:py-20 lg:py-28" aria-labelledby="outside-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 id="outside-heading" className="text-white font-bold text-2xl sm:text-3xl md:text-4xl mb-6 leading-tight">
                Why it comes from outside
              </h2>
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                Even where your own people see it clearly, the same observation lands differently depending on who is understood to have made it, and in a senior appointment or anything politically difficult that difference decides whether the evidence gets used at all. We write the observation and the conclusion in our own words and stand behind them, and when we are wrong we say so and correct the record.
              </p>
            </Reveal>
          </div>
        </section>

        {/* 7. HOW AN ENGAGEMENT RUNS */}
        <section className="bg-[#f0f5f8] py-14 sm:py-20 lg:py-28" aria-labelledby="engagement-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 id="engagement-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-10 sm:mb-12 leading-tight">
                How an engagement runs
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <EngagementFlow />
            </Reveal>
            <Reveal delay={150}>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mt-10 sm:mt-14 max-w-3xl">
                We deliver through coaching, leadership development and training, and facilitation, all by credentialled practitioners from the RESILIGENT group.
              </p>
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
                <a href="/coaching" className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300">
                  Coaching <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a href="/capability-building" className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300">
                  Leadership development and training <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a href="/engagement" className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300">
                  How an engagement works <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* 8. EXAMPLES OF OUR WORK */}
        <section className="bg-white py-14 sm:py-20 lg:py-28" aria-labelledby="examples-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 id="examples-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-8 leading-tight">
                Examples of our work
              </h2>
              <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mb-10">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-7">
                  <h3 className="text-slate-900 font-semibold text-lg mb-3">Financial services</h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    A medium-sized financial services firm found its strategy was not being taken up below the top team. The senior leaders were focused on competing while the level below them, who had to interpret and implement it, were focused on surviving, and the caution between them meant the room agreed with whatever came down from above while implementation slowed. The client reported that 85% of what was agreed was still being tracked and delivered nine months later.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-7">
                  <h3 className="text-slate-900 font-semibold text-lg mb-3">Energy</h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    In an energy business, leaders had a style that dominated the room without their knowing it. Others worked differently and stopped putting things forward, so ideas stopped moving because of how the interactions landed rather than because of the ideas themselves. Once that was on the table the team could plan around it, and the client reported that most of what was agreed was still moving six months on.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="mb-8">
                <ClientTestimonial
                  quote="RESILIGENT delivered leadership development from frontline teams through to board level. The experience was practical, refreshing, and sufficiently valuable that I would recommend it as a regular organisational investment."
                  name="Omar Shekarau"
                  title="Executive Director"
                  company="Bank of Industry"
                  variant="light"
                />
              </div>
              <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg mb-8">
                <iframe
                  src="https://www.youtube.com/embed/osAqH1hVklo"
                  title="RESILIGENT Client Case Study - Bank of Industry"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <a 
                href="/case-studies"
                className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300"
              >
                More of our work
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            </Reveal>
          </div>
        </section>

        {/* 9. WHO WE ARE */}
        <section className="bg-slate-100 py-14 sm:py-20 lg:py-28" aria-labelledby="who-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 id="who-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-6 leading-tight">
                Who we are
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-5">
                RESILIGENT was founded in 2010 and has offices in Lagos, London and Dubai. Four ICF Master Certified Coaches work across the RESILIGENT group, and the wider faculty is credentialled at ACC, PCC and MCC level. Most of our practitioners are African and working on the continent, and credentials are verifiable for everyone we put in front of you.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-8">
                Our founder, Remi Adebonojo, worked at Oliver Wyman early in his career, reaching senior level, and went on to become a chief risk officer accountable for major programmes across the UK, Europe, Asia and Africa. He is one of fewer than seven ICF Master Certified Coaches in West Africa.
              </p>
              <a 
                href="/about"
                className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300"
              >
                About RESILIGENT
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            </Reveal>
          </div>
        </section>

        {/* 10. CLOSING BAND */}
        <section className="bg-[#e8f2f7] py-14 sm:py-20 lg:py-28" aria-labelledby="closing-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 id="closing-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                Tell us the decision you are closest to making
              </h2>
              <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
                We will say what evidence it requires, what that would cost and how long it would take, and nothing is chargeable until you agree the scope.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 sm:gap-3 bg-[#2693d0] text-white px-6 sm:px-10 py-4 sm:py-5 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300"
                  data-testid="closing-book-btn"
                >
                  Book a conversation
                  <ArrowRight size={20} aria-hidden="true" />
                </a>
                <button
                  onClick={() => setShowDownloadModal(true)}
                  className="inline-flex items-center gap-2 sm:gap-3 bg-white text-[#2693d0] border-2 border-[#2693d0] px-6 sm:px-10 py-4 sm:py-5 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#2693d0]/5 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300"
                  aria-label="Download RESILIGENT Corporate Profile"
                  data-testid="download-profile-btn"
                >
                  Download Corporate Profile
                  <Download size={20} aria-hidden="true" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {showImpactFilm && <ImpactFilmModal onClose={() => setShowImpactFilm(false)} />}

        {/* Download Modal (restored) */}
        {showDownloadModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowDownloadModal(false)}>
            <div 
              className="bg-[#ffffff] rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200"
              style={{ backgroundColor: '#ffffff' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-slate-900 font-bold text-xl sm:text-2xl">Download Corporate Profile</h3>
                <button 
                  onClick={() => setShowDownloadModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-slate-600 text-sm sm:text-base mb-6">
                Please provide your details to download our corporate profile.
              </p>
              <form onSubmit={handleDownloadSubmit} className="space-y-4">
                <div>
                  <label htmlFor="download-name" className="block text-slate-700 text-sm font-medium mb-1">
                    Full Name *
                  </label>
                  <input
                    id="download-name"
                    type="text"
                    required
                    value={downloadForm.name}
                    onChange={(e) => setDownloadForm({...downloadForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="download-company" className="block text-slate-700 text-sm font-medium mb-1">
                    Company Name *
                  </label>
                  <input
                    id="download-company"
                    type="text"
                    required
                    value={downloadForm.company}
                    onChange={(e) => setDownloadForm({...downloadForm, company: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label htmlFor="download-email" className="block text-slate-700 text-sm font-medium mb-1">
                    Email Address *
                  </label>
                  <input
                    id="download-email"
                    type="email"
                    required
                    value={downloadForm.email}
                    onChange={(e) => setDownloadForm({...downloadForm, email: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="your.email@company.com"
                  />
                </div>
                {downloadError && (
                  <p className="text-red-500 text-sm">{downloadError}</p>
                )}
                <button
                  type="submit"
                  disabled={downloadLoading}
                  className="w-full bg-[#2693d0] text-white py-3 rounded-lg font-semibold hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="download-submit-btn"
                >
                  {downloadLoading ? 'Processing...' : (
                    <>
                      Download Profile
                      <Download size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
